import { Component, Inject, OnInit } from '@angular/core';
import { L10N_LOCALE, L10nLocale } from 'angular-l10n';
import { ComponentLookup } from 'src/app/shared/decorators/component-lookup.decorator';
import { PublicProfileAvatarComponentModel, PublicProfileToPublicProfileAvatarComponentModelConverter } from '../../public-profile-avatar/public-profile-avatar.component';
import { IControlComponent } from 'src/app/shared/interfaces/control-component';
import { BaseService } from 'src/app/core/services/base.service';
import { PatientVerificationComponentData, PatientVerificationDataModel } from 'src/app/shared/models/workflow/models/patient-verification-data.model';
import { ComponentDataSetMessage, ComponentFinishLoadingMessage, MessageBusService } from 'src/app/core/services/message-bus.service';
import { ComponentName } from 'src/app/shared/enums/component-name.enum';
import { AbstractControl, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { UserService } from 'src/app/core/services/user.service';
import * as moment from 'moment';
import { PatientVerificationResponse } from 'src/app/shared/interfaces/patient-verification-response';
import { RequestsToVerifyService } from 'src/app/core/services/requests-to-verify.service';

@Component({
  selector: 'app-patient-verification',
  templateUrl: './patient-verification.component.html',
  styleUrls: ['./patient-verification.component.css']
})
@ComponentLookup(ComponentName.PATIENT_VERIFICATION)
export class PatientVerificationComponent implements IControlComponent, OnInit {

  avatarModel = new PublicProfileAvatarComponentModel();
  componentData: PatientVerificationComponentData;
  data: any;
  errorMessage: string;
  formulario: FormGroup;
  isCliniweb: boolean;
  loading: boolean = false;
  sending: boolean = false;
  maxDate: Date;
  minDate: Date;
  model = new  PatientVerificationDataModel;
  patientName = '';
  titleTextRefence = 'shared.medicalOrder.patientVerification.title';

  constructor(
    @Inject(L10N_LOCALE) public locale: L10nLocale,
    private baseService: BaseService,
    private formBuilder: FormBuilder,
    private requestsToVerifyService: RequestsToVerifyService,
    private messageBusService: MessageBusService,
    private userService: UserService
  ) {
    this.configBirthday();
  }

  private configBirthday(): void {
    const currentYear = new Date().getFullYear();
    this.maxDate = new Date();
    this.minDate = new Date(currentYear - 120, 0, 1);
  }

  ngOnInit(): void {
    this.parseControlData();
    this.isCliniweb = this.baseService.isCliniwebCompany();
    this.sendComponentFinishLoadingMessage();
  }

  private parseControlData(): void {
    this.loading = true;

    if (this.data && this.data.configurationData) {
      this.model = this.data.configurationData as PatientVerificationDataModel;
      this.componentData = this.model.configData;      
      this.patientName = this.model.nombrePaciente;
      this.avatarModel = new PublicProfileToPublicProfileAvatarComponentModelConverter(this.model.profile);

      this.formulario = this.formBuilder.group({
        patientBirthdate: [null, Validators.required]
      });
    }

    this.loading = false;
  }

  private sendComponentFinishLoadingMessage(): void {
    if (!this.data) return;

    const event = new ComponentFinishLoadingMessage();
    
    event.idFormulario = this.data.idFormulario;
    event.idControl = this.data.idControlPadre;

    this.messageBusService.componentFinishLoading(event);   
  }

  controlHasError(controlName: string, errorName: string): boolean {
    return this.getControl(controlName).hasError(errorName);
  }

  private getControl(controlName: string): AbstractControl {
    return this.formulario.controls[controlName];
  }

  enviar(): void {
    try{
      this.formulario.markAllAsTouched();
    
      if (this.formulario.invalid) return;
  
      let patientBirthdate = this.formulario.controls.patientBirthdate.value;

      patientBirthdate = this.normalizeDate(patientBirthdate);

      // Verificar que la fecha está correctamente normalizada
      if (!patientBirthdate) {
        throw new Error('La fecha de nacimiento es inválida.');
      }
  
      this.sending = true;
      this.formulario.disable();
  
      this.requestsToVerifyService.validate(patientBirthdate, this.model.idVerificacion).subscribe({
        next: (response) => {
          console.log('validación de la fecha de nacimiento recibida:', response);
          this.validateNext(response);
        },
        error: (error) => {
          const errorMessage = error?.message || 'Error desconocido al validar la fecha de nacimiento.';
          const errorDetails = error?.error?.message || error?.statusText || 'No se proporcionaron detalles adicionales.';
  
          alert(`Error al validar la fecha de nacimiento: ${errorMessage}\nDetalles: ${errorDetails}`);

          this.formulario.enable();
          this.sending = false;
        },
        complete: () => {
           this.formulario.enable();
           this.sending = false; 
        }
      });
    }
    catch (error) {
      this.formulario.enable();
      this.sending = false;
    
      const err = error as Error; // Aserción de tipo a Error
    
      const errorMessage = err?.message || 'Error desconocido al validar la fecha de nacimiento.';
      const errorDetails = err?.stack || 'No se proporcionaron detalles adicionales.';
    
      alert(`Error al validar la fecha de nacimiento: ${errorMessage}\nDetalles: ${errorDetails}`);
    }
    
  }

  private normalizeDate(date: Date | moment.Moment | string): string | null {
    if (typeof date === 'string') {
      // Si ya es un string, solo verificar el formato 'YYYY-MM-DD'
      return this.isValidDateString(date) ? date : null;
    } else if (date instanceof Date) {
      // Si es un objeto Date, convertirlo a string 'YYYY-MM-DD'
      return date.toISOString().split('T')[0];
    } else if (moment.isMoment(date)) {
      // Si es un objeto Moment, convertirlo a string 'YYYY-MM-DD'
      return date.format('YYYY-MM-DD');
    }
  
    return null;
  }
  
  private isValidDateString(dateString: string): boolean {
    const regex = /^\d{4}-\d{2}-\d{2}$/; // Formato 'YYYY-MM-DD'
    return regex.test(dateString);
  }

  private validateNext(args: PatientVerificationResponse): void {
    let message = new ComponentDataSetMessage();
      
    message.componentName = ComponentName.PATIENT_VERIFICATION;
    message.data = args;

    if (!args.isValid) {
      this.errorMessage = 'shared.medicalOrder.patientVerification.text6';
    } else {
      this.messageBusService.onComponentDataSetMessage(message);
    }
  } 

  getBirthdateControl(): FormControl | null{
    return this.formulario.get('patientBirthdate') ? this.formulario.get('patientBirthdate') as FormControl : null;
  }
}
